import React from "react";
import MuiTypography from "@mui/material/Typography";
import PropTypes from "prop-types";

/**
 * Typography component wrapper for consistent text rendering across the application.
 * Wraps Material-UI Typography with theme integration.
 * 
 * @component
 * @example
 * <Typography variant="h1">Page Title</Typography>
 * <Typography variant="body1" color="error">Error message</Typography>
 */
const Typography = ({ children, ...props }) => {
  return <MuiTypography {...props}>{children}</MuiTypography>;
};

Typography.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * Applies the theme typography styles
   */
  variant: PropTypes.oneOf([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "subtitle1",
    "subtitle2",
    "body1",
    "body2",
    "caption",
    "button",
    "overline",
    "label",
    "labelOutline",
    "display",
    "error",
    "link",
    "updatedH3",
  ]),
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * If true, the text will have a bottom margin
   */
  gutterBottom: PropTypes.bool,
  /**
   * Set the text-align on the component
   */
  align: PropTypes.oneOf(["left", "center", "right", "justify", "inherit"]),
  /**
   * The color of the component
   */
  color: PropTypes.string,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI Typography behavior
// MUI Typography default: variant="body1"

export default Typography;

